import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Link2, Search, RefreshCw, ScrollText, Wand2, CheckCircle2, AlertTriangle, HelpCircle, Save, X, FileDown, AlertCircle } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATS = {
  auto: { label: 'Mapeados automaticamente', icon: CheckCircle2, color: 'text-green-400', badge: 'bg-green-900/40 border-green-700/60 text-green-300' },
  review: { label: 'Precisam de revisão manual', icon: AlertTriangle, color: 'text-amber-400', badge: 'bg-amber-900/40 border-amber-700/60 text-amber-300' },
  ambiguous: { label: 'Duplicados ou ambíguos', icon: HelpCircle, color: 'text-fuchsia-400', badge: 'bg-fuchsia-900/40 border-fuchsia-700/60 text-fuchsia-300' },
  pending: { label: 'Mapeamento pendente', icon: X, color: 'text-gray-400', badge: 'bg-zinc-800 border-zinc-700 text-gray-400' },
};

const AdminHotmartMapping = () => {
  const [rows, setRows] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);

  // Estado de revisão do auto-mapeamento
  const [running, setRunning] = useState(false);
  const [review, setReview] = useState(null); // { report, edits: {product_id: {nome, category, program_code, suggested, score, candidates}} }
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [m, p] = await Promise.all([
        axios.get(`${API}/admin/hotmart/mappings`, { withCredentials: true }),
        axios.get(`${API}/admin/programs`, { withCredentials: true }),
      ]);
      setRows(m.data);
      setPrograms(p.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao carregar mapeamentos');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const setMapping = async (product_id, program_code) => {
    try {
      await axios.put(`${API}/admin/hotmart/mappings/${product_id}`, { program_code: program_code || null }, { withCredentials: true });
      setRows(rows.map(r => r.hotmart_product_id === product_id ? { ...r, program_code: program_code || null } : r));
      toast.success(program_code ? 'Mapeamento salvo' : 'Mapeamento removido');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const loadLogs = async () => {
    try {
      const res = await axios.get(`${API}/admin/hotmart/operations-log`, { withCredentials: true });
      setLogs(res.data);
      setShowLogs(true);
    } catch (e) { toast.error('Erro ao carregar log'); }
  };

  const exportCSV = async () => {
    try {
      const res = await axios.get(`${API}/admin/hotmart/mappings/export`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `mapeamento_hotmart_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exportado');
    } catch (e) { toast.error('Erro ao exportar CSV'); }
  };

  const exportReviewPDF = () => {
    if (!review) return;
    const rep = review.report;
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    doc.setFontSize(16); doc.setTextColor('#dc2626');
    doc.text('AJ | BIM ACADEMY — Relatório de Mapeamento Hotmart', 14, 18);
    doc.setFontSize(10); doc.setTextColor('#333');
    doc.text(`Gerado em ${new Date().toLocaleString('pt-BR')}`, 14, 25);
    doc.text(`Total: ${rep.total}  ·  Automáticos: ${rep.auto}  ·  Revisão: ${rep.review}  ·  Ambíguos: ${rep.ambiguous}  ·  Pendentes: ${rep.pending}`, 14, 31);
    const catLabel = { auto: 'Automático', review: 'Revisão', ambiguous: 'Ambíguo', pending: 'Pendente' };
    const body = Object.entries(review.edits).map(([pid, v]) => {
      const prog = programs.find(p => p.program_code === v.program_code);
      return [v.nome, catLabel[v.category], v.score ? `${v.score}%` : '—', v.program_code ? `${v.program_code} · ${prog?.name || ''}` : 'Pendente'];
    });
    autoTable(doc, {
      startY: 37,
      head: [['Produto Hotmart', 'Categoria', 'Sim.', 'Programa Educacional']],
      body,
      styles: { fontSize: 7, cellPadding: 1.5 },
      headStyles: { fillColor: [24, 24, 27] },
      columnStyles: { 0: { cellWidth: 70 }, 3: { cellWidth: 70 } },
    });
    doc.save(`relatorio_mapeamento_${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success('PDF exportado');
  };

  const runAutoMap = async () => {
    setRunning(true);
    try {
      const res = await axios.post(`${API}/admin/hotmart/auto-map`, {}, { withCredentials: true });
      const { auto, review: rev, ambiguous, pending, report } = res.data;
      const edits = {};
      const add = (list, category) => list.forEach(it => {
        edits[it.hotmart_product_id] = {
          nome: it.nome,
          status: it.status,
          category,
          // pré-seleciona a sugestão para auto; revisão/ambíguo/pendente começam pela sugestão (pode limpar)
          program_code: it.current_program_code || (category === 'auto' ? it.suggested_program_code : (it.suggested_program_code || '')),
          suggested: it.suggested_program_code,
          suggested_name: it.suggested_program_name,
          score: it.score,
          candidates: it.candidates || [],
        };
      });
      add(auto, 'auto');
      add(rev, 'review');
      add(ambiguous, 'ambiguous');
      add(pending, 'pending');
      setReview({ report, edits });
      toast.success('Mapeamento automático concluído — revise antes de salvar');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao executar mapeamento automático');
    } finally { setRunning(false); }
  };

  const editSelect = (pid, program_code) => {
    setReview(prev => ({ ...prev, edits: { ...prev.edits, [pid]: { ...prev.edits[pid], program_code } } }));
  };

  const saveReview = async () => {
    setSaving(true);
    try {
      const mappings = Object.entries(review.edits).map(([pid, v]) => ({
        hotmart_product_id: pid,
        program_code: v.program_code || null,
      }));
      const res = await axios.post(`${API}/admin/hotmart/mappings/save`, { mappings }, { withCredentials: true });
      toast.success(`Salvo: ${res.data.saved} mapeados, ${res.data.removed} removidos`);
      setReview(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar mapeamentos');
    } finally { setSaving(false); }
  };

  const programSelect = (value, onChange, testid) => (
    <select
      className={`w-full bg-zinc-800 border rounded-md h-9 px-2 text-sm ${value ? 'border-green-600/50 text-white' : 'border-zinc-700 text-gray-400'}`}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      data-testid={testid}
    >
      <option value="">— não mapeado (pendente) —</option>
      {programs.map(p => <option key={p.id} value={p.program_code}>{p.program_code} · {p.name}</option>)}
    </select>
  );

  const filtered = rows.filter(r =>
    !q || r.nome?.toLowerCase().includes(q.toLowerCase()) || r.hotmart_product_id.includes(q));
  const mappedCount = rows.filter(r => r.program_code).length;

  // ---------- Tela de revisão ----------
  if (review) {
    const grouped = { auto: [], review: [], ambiguous: [], pending: [] };
    Object.entries(review.edits).forEach(([pid, v]) => grouped[v.category].push([pid, v]));
    const rep = review.report;
    return (
      <div data-testid="admin-hotmart-mapping-review">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h3 className="text-xl font-bold text-white flex items-center gap-2"><Wand2 className="text-fuchsia-500" size={20} />Revisão do mapeamento automático</h3>
            <p className="text-gray-400 text-sm">Confira as sugestões, ajuste o que precisar e clique em Salvar. Limiares: auto ≥ {rep.auto_threshold}% · revisão ≥ {rep.review_threshold}%.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={exportCSV} data-testid="review-export-csv"><FileDown size={16} className="mr-2" />CSV</Button>
            <Button variant="outline" onClick={exportReviewPDF} data-testid="review-export-pdf"><FileDown size={16} className="mr-2" />PDF</Button>
            <Button variant="outline" onClick={() => setReview(null)} data-testid="cancel-review"><X size={16} className="mr-2" />Cancelar</Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={saveReview} disabled={saving} data-testid="save-mappings"><Save size={16} className="mr-2" />{saving ? 'Salvando...' : 'Salvar mapeamentos'}</Button>
          </div>
        </div>

        {/* Relatório final */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="auto-map-report">
          {[['total', 'Total', 'text-white'], ['auto', 'Automáticos', 'text-green-400'], ['review', 'Revisão', 'text-amber-400'], ['ambiguous', 'Ambíguos', 'text-fuchsia-400'], ['pending', 'Pendentes', 'text-gray-400']].map(([k, label, color]) => (
            <div key={k} className="bg-black/40 border border-zinc-800 rounded-lg p-3 text-center">
              <div className={`text-2xl font-bold ${color}`}>{rep[k]}</div>
              <div className="text-xs text-gray-500">{label}</div>
            </div>
          ))}
        </div>

        {['auto', 'review', 'ambiguous', 'pending'].map(cat => {
          const list = grouped[cat];
          if (!list.length) return null;
          const C = CATS[cat];
          const Icon = C.icon;
          return (
            <div key={cat} className="mb-6" data-testid={`review-group-${cat}`}>
              <h4 className={`flex items-center gap-2 font-semibold mb-2 ${C.color}`}><Icon size={16} />{C.label} <span className="text-gray-500 text-sm">({list.length})</span></h4>
              <div className="overflow-x-auto border border-zinc-800 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-900/80">
                    <tr className="text-left text-gray-400 border-b border-zinc-800">
                      <th className="py-2 px-3">Produto Hotmart</th>
                      <th className="py-2 px-3 w-24">Similaridade</th>
                      <th className="py-2 px-3 w-72">Programa Educacional</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map(([pid, v]) => (
                      <tr key={pid} className="border-b border-zinc-800/60 text-gray-200" data-testid={`review-row-${pid}`}>
                        <td className="py-2 px-3">
                          <div>{v.nome}</div>
                          <div className="font-mono text-xs text-gray-500">{pid}</div>
                        </td>
                        <td className="py-2 px-3">
                          {v.score > 0 ? <span className={`px-2 py-0.5 rounded border text-xs ${C.badge}`}>{v.score}%</span> : <span className="text-gray-600 text-xs">—</span>}
                          {cat === 'ambiguous' && v.candidates?.length > 1 && (
                            <div className="text-[10px] text-gray-500 mt-1">vs {v.candidates[1].program_code} ({v.candidates[1].score}%)</div>
                          )}
                        </td>
                        <td className="py-2 px-3">
                          {programSelect(v.program_code, val => editSelect(pid, val), `review-select-${pid}`)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // ---------- Tela principal (lista + edição manual) ----------
  return (
    <div data-testid="admin-hotmart-mapping">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h3 className="text-xl font-bold text-white flex items-center gap-2"><Link2 className="text-red-500" size={20} />Mapeamento Hotmart × Programas</h3>
          <p className="text-gray-400 text-sm">Associe cada produto Hotmart a um Programa Educacional. {mappedCount}/{rows.length} mapeados.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button className="bg-fuchsia-600 hover:bg-fuchsia-700" onClick={runAutoMap} disabled={running} data-testid="run-auto-map">
            <Wand2 size={16} className="mr-2" />{running ? 'Analisando...' : 'Executar mapeamento automático'}
          </Button>
          <Button variant="outline" onClick={exportCSV} data-testid="export-csv"><FileDown size={16} className="mr-2" />Exportar CSV</Button>
          <Button variant="outline" onClick={load}><RefreshCw size={16} className="mr-2" />Atualizar</Button>
          <Button variant="outline" onClick={loadLogs} data-testid="view-operations-log"><ScrollText size={16} className="mr-2" />Log de operações</Button>
        </div>
      </div>

      {!loading && (rows.length - mappedCount) > 0 && (
        <div className="flex items-start gap-3 bg-amber-950/40 border border-amber-700/60 rounded-lg p-3 mb-4" data-testid="unmapped-alert">
          <AlertCircle className="text-amber-400 mt-0.5 shrink-0" size={18} />
          <div className="text-sm">
            <span className="text-amber-300 font-semibold">{rows.length - mappedCount} produto(s) Hotmart sem mapeamento.</span>
            <span className="text-amber-200/80"> Vendas desses produtos não geram matrícula automática. Clique em "Executar mapeamento automático" para vinculá-los.</span>
          </div>
        </div>
      )}

      <div className="relative mb-4 max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <Input className="bg-zinc-800 border-zinc-700 text-white pl-9" placeholder="Buscar por nome ou ID..." value={q} onChange={e => setQ(e.target.value)} data-testid="mapping-search" />
      </div>

      {showLogs && (
        <div className="bg-black/40 border border-zinc-700 rounded-lg p-4 mb-4 max-h-64 overflow-y-auto" data-testid="operations-log">
          <div className="flex justify-between items-center mb-2">
            <span className="text-white font-semibold text-sm">Últimas operações automáticas</span>
            <button onClick={() => setShowLogs(false)} className="text-gray-400 text-xs">fechar</button>
          </div>
          {logs.length === 0 ? <p className="text-gray-500 text-xs">Nenhuma operação registrada ainda.</p> : (
            <table className="w-full text-xs">
              <tbody>
                {logs.map(l => (
                  <tr key={l.id} className="border-b border-zinc-800/50 text-gray-300">
                    <td className="py-1 pr-2 text-gray-500">{new Date(l.created_at).toLocaleString('pt-BR')}</td>
                    <td className="py-1 pr-2 font-mono">{l.transaction}</td>
                    <td className="py-1 pr-2">{l.step}</td>
                    <td className="py-1 pr-2"><span className={l.status === 'done' ? 'text-green-400' : l.status === 'warning' ? 'text-amber-400' : 'text-gray-400'}>{l.status}</span></td>
                    <td className="py-1">{l.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {loading ? <p className="text-gray-400">Carregando...</p> : (
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-zinc-900">
              <tr className="text-left text-gray-400 border-b border-zinc-800">
                <th className="py-2 px-2">ID Hotmart</th><th className="py-2 px-2">Produto</th>
                <th className="py-2 px-2">Status</th><th className="py-2 px-2 w-64">Programa Educacional</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.hotmart_product_id} className="border-b border-zinc-800/60 text-gray-200" data-testid={`mapping-row-${r.hotmart_product_id}`}>
                  <td className="py-2 px-2 font-mono text-xs">{r.hotmart_product_id}</td>
                  <td className="py-2 px-2">{r.nome}</td>
                  <td className="py-2 px-2">
                    <span className={r.status === 'ACTIVE' ? 'text-green-400' : 'text-amber-400'}>{r.status}</span>
                    {!r.program_code && <span className="ml-2 text-xs text-gray-500">· pendente</span>}
                  </td>
                  <td className="py-2 px-2">
                    {programSelect(r.program_code, val => setMapping(r.hotmart_product_id, val), `mapping-select-${r.hotmart_product_id}`)}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan="4" className="py-6 text-center text-gray-500">Nenhum produto. Sincronize os produtos na aba "Programas".</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminHotmartMapping;
